import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { ColDef, ValueFormatterParams, ValueGetterParams } from "@ag-grid-community/core";
import { Check, Close, RemoveCircleOutline } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";

import { SEARCH_KEY } from "fond/constants";
import { useEntityUtil } from "fond/hooks/useEntityUtil";
import { useStarred } from "fond/projects/ItemMenu/hooks/useStarred";
import { ResourceEntity, Store } from "fond/types";
import { GridId } from "fond/types/grids";
import { dateComparator, dateFilterComparator, dateValueFormatter } from "fond/widgets/AgGrid";

import EntityTypeCellRenderer from "./ProjectList/EntityCellRenderer";
import RowMenuCellRenderer from "./ProjectList/RowMenuCellRenderer";
import StatusCellRenderer from "./ProjectList/StatusCellRenderer";

type UseProjectsTableReturn = {
  id: GridId;
  columns: ColDef[];
};

interface IProps {
  openRowMenu(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, entity?: ResourceEntity): void;
}

const designGenerationStatusEntries = {
  PENDING: null, // Should not be observable through the API
  IN_PROGRESS: {
    text: "Generating",
    icon: <CircularProgress size={18} color="info" />,
  },
  COMPLETE: {
    text: "Created",
    icon: <Check />,
  },
  ERROR: {
    text: "Error",
    icon: <Close />,
  },
  CANCELLED: {
    text: "Cancelled",
    icon: <RemoveCircleOutline />,
  },
};

const useProjectsTable = ({ openRowMenu }: IProps): UseProjectsTableReturn => {
  const { search, pathname } = useLocation();
  const { filters, account } = useSelector((state: Store) => state.projects);
  const currentUsername = useSelector((state: Store) => state.cognito.user?.username);
  const { starred } = useStarred();
  const { getEntityLabel } = useEntityUtil();

  const isSearchPage = search.startsWith(`?${SEARCH_KEY}=`);
  const isStarred = useCallback((id: string) => starred.includes(id), [starred]);
  const isCityPlannerFolder = pathname.startsWith("/cities/");

  const id: GridId = useMemo(() => {
    if (isSearchPage) return GridId.SEARCH;
    if (isCityPlannerFolder) return GridId.CITY_PLANNER_SUMMARY;
    return { workspace: GridId.WORKSPACE, recent: GridId.RECENT_PROJECTS, shared: GridId.SHARED_PROJECTS, starred: GridId.STARRED_PROJECTS }[filters];
  }, [isSearchPage, isCityPlannerFolder, filters]);

  /**
   * Return a filtered set of grid colums based on current view (e.g. workspace, shared, recent, etc...)
   *
   * @param cols - All grid columns
   * @returns {ColDef[]} Column definitions for a grid
   */
  const filterColumns = useCallback(
    (columns: ColDef[]) => {
      let cols = columns;

      if (isCityPlannerFolder) {
        cols = cols.filter((col) => col.field !== "EntityType");
      }

      if (filters === "recent") return cols;

      // Remove 'LastViewed' column if view is 'shared' or 'search' related
      if ((filters === "shared" && !account) || isSearchPage) {
        return cols.filter((col) => col.field !== "LastViewed");
      }

      // Remove 'Account' and 'LastViewed' columns
      return cols.filter((col) => col.field && !["Account.Name", "LastViewed"].includes(col.field));
    },
    [account, filters, isCityPlannerFolder, isSearchPage]
  );

  /**
   * Return set of grid colums based on current view (e.g. workspace, shared, recent, etc...)
   *
   * @param cols - All grid columns
   * @returns Column definitions for a grid
   */
  const sortColumns = (cols: ColDef[]): ColDef[] => {
    const sortedColumns = cols;
    const lastViewed = cols.find((col) => col.field === "LastViewed");
    const lastModified = cols.find((col) => col.field === "LastModified");

    if (lastViewed) {
      lastViewed.sort = "desc";
      delete lastModified?.sort;
    }
    if (!lastViewed && lastModified) {
      lastModified.sort = "desc";
    }

    return sortedColumns;
  };

  const allColumns: ColDef[] = useMemo(
    () => [
      {
        field: "Name",
        headerName: "Title",
        flex: 3,
        minWidth: 200,
        cellRenderer: EntityTypeCellRenderer,
        cellRendererParams: {
          currentUsername,
          isStarred,
        },
        valueGetter: (params: ValueGetterParams<ResourceEntity>) =>
          params.data?.EntityType === "project" ? params.data?.ProjectName : params.data?.Name,
        comparator(a, b) {
          return a.localeCompare(b);
        },
      },
      {
        field: "Status",
        cellRendererSelector: ({ data }) => {
          if (data?.EntityType === "project") {
            const state = data?.Status?.CancelTime ? "CANCELLED" : data?.Status?.State || data?.MultiProjectArea?.ImportStatus?.State;
            if (state) {
              return {
                params: { state, stateEntries: designGenerationStatusEntries },
                component: StatusCellRenderer,
              };
            }
          }

          // Reports also have a status, but we don't have polling on the getReports query yet
          // so the status is never updated. For now, don't include the status cell renderer for reports.
          return { component: () => null };
        },
        headerName: "Status",
        width: 125,
      },
      {
        field: "Account.Name",
        headerName: "Account",
        flex: 1,
      },
      {
        field: "EntityType",
        headerName: "Type",
        width: 150,
        valueGetter: (params: ValueGetterParams<ResourceEntity>) => getEntityLabel(params.data),
      },
      {
        field: "LastModifiedBy",
        headerName: "Last modified by",
        flex: 1,
        valueFormatter: (params: ValueFormatterParams) => {
          let username = params.data?.LastModifiedBy || params.data?.Creator;
          if (params.data?.EntityType === "report") username = username.Email;
          return username === currentUsername ? "me" : username;
        },
      },
      {
        comparator: dateComparator,
        field: "LastModified",
        headerName: "Last modified",
        width: 170,
        filter: "agDateColumnFilter",
        filterParams: {
          debounceMs: 500,
          suppressAndOrCondition: true,
          comparator: dateFilterComparator,
        },
        valueFormatter: dateValueFormatter,
      },
      {
        comparator: dateComparator,
        field: "LastViewed",
        headerName: "Last viewed",
        width: 170,
        filter: "agDateColumnFilter",
        filterParams: {
          debounceMs: 500,
          suppressAndOrCondition: true,
          comparator: dateFilterComparator,
        },
        valueFormatter: dateValueFormatter,
      },
      {
        field: "menu",
        headerName: "Menu",
        cellRenderer: RowMenuCellRenderer,
        cellRendererParams: {
          onClick: openRowMenu,
        },
        headerComponentParams: { displayName: "" },
        lockVisible: true,
        suppressHeaderMenuButton: true,
        sortable: false,
        resizable: false,
        type: "rightAligned",
        width: 74,
      },
    ],
    [currentUsername, isStarred, openRowMenu]
  );

  const filteredColumns = useMemo(() => filterColumns(allColumns), [filterColumns, allColumns]);
  const filteredAndSortedColumns = sortColumns(filteredColumns);

  return { columns: filteredAndSortedColumns, id };
};

export default useProjectsTable;
